import React, { FC, useCallback, useMemo } from 'react';
import { Select } from 'antd';
import {
	choiceLevelFollowUpActionsExist,
	FOLLOW_UP_ACTION_OPTIONS,
	FollowUpAction,
	getFollowUpActionsForBE,
} from '../../utils/SiteSurveyUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { CustomCellProps } from './ImportGridTypes';

// Cell for the task-level "Follow-up actions" column. Unlike the per-choice
// follow-ups (which live inside MultipleChoicesField), this applies to every
// answer type — checkbox, checklist, free text, multiple choice — so there is no
// questionType gating here. The value round-trips as an array of action values;
// a CSV upload hands us a "; "-joined label string instead, which we normalize
// back to values before rendering.
const normalizeFollowUpActions = (value: any): FollowUpAction[] => {
	const { followUpActions } = getFollowUpActionsForBE(value);
	return followUpActions || [];
};

const FollowUpActionsField: FC<CustomCellProps> = ({ value, row, onChange }) => {
	const selected = useMemo(() => normalizeFollowUpActions(value), [value]);

	// Task-level and per-option follow-up actions are mutually exclusive. When the
	// row's multiple-choice options already carry their own follow-ups, lock this
	// column so the two levels can't both be set. The "do they exist?" decision
	// lives in the walkthrough utils; this cell only flips enabled/disabled.
	const disabled = useMemo(
		() => choiceLevelFollowUpActionsExist(nullSafeGet('answerOptions', row)),
		[row]
	);

	const handleChange = useCallback(
		(nextActions: FollowUpAction[]) => {
			onChange(Array.isArray(nextActions) ? nextActions : []);
		},
		[onChange]
	);

	// react-data-grid selects + refocuses the cell on click. With an interactive
	// Select rendered inline (not behind a portal modal like the other custom
	// cells), that refocus blurs the just-opened dropdown and snaps it shut on the
	// first click. Stop the event from reaching the grid so the Select keeps focus
	// and opens on the first click.
	const stopGridSelection = useCallback((event: React.MouseEvent) => {
		event.stopPropagation();
	}, []);

	return (
		<div
			className="flex h-full w-full items-center overflow-hidden"
			onMouseDown={stopGridSelection}
			onClick={stopGridSelection}
			title={
				disabled
					? 'Follow-up actions are set on individual answer options. Clear them to set task-level actions.'
					: ''
			}
		>
			<Select
				mode="multiple"
				placeholder={disabled ? 'Set on options' : 'None'}
				value={selected}
				onChange={handleChange}
				options={FOLLOW_UP_ACTION_OPTIONS}
				className="w-full"
				bordered={false}
				maxTagCount="responsive"
				disabled={disabled}
			/>
		</div>
	);
};

export const renderFollowUpActionsCell = (props: CustomCellProps): React.ReactNode => (
	<FollowUpActionsField {...props} />
);

export default FollowUpActionsField;
